import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api, { apiError } from '../../services/api';
import { useApi } from '../../hooks/useApi';
import Select from '../../components/ui/Select';
import { Card, Field, LoadingRows, Notice, PageHeader, Toggle } from '../../components/ui/Page';
import { CodeChip } from '../../components/ui/CountrySelect';
import type { AiModel } from './index';

type GeminiModel = { id: string; label: string; description: string | null; inputTokenLimit: number | null };
type Detail = AiModel & { prompts: { id: string; countryCode: string; countryName: string }[] };

type Form = { model: string; label: string; description: string; strengths: string; sortOrder: string; isActive: boolean; isDefault: boolean };
const EMPTY: Form = { model: '', label: '', description: '', strengths: '', sortOrder: '10', isActive: true, isDefault: false };

export default function AiModelFormPage() {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const detail = useApi<{ provider: Detail }>(editing ? `/admin/ai/providers/${id}` : null);
  const catalog = useApi<{ source: 'google' | 'fallback'; models: GeminiModel[] }>('/admin/ai/gemini-models');
  const [form, setForm] = useState<Form>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const p = detail.data?.provider;

  useEffect(() => {
    if (!p) return;
    setForm({
      model: p.model,
      label: p.label,
      description: p.description || '',
      strengths: p.strengths || '',
      sortOrder: String(p.sortOrder),
      isActive: p.isActive,
      isDefault: p.isDefault,
    });
  }, [p]);

  const models = catalog.data?.models || [];
  const options = [
    ...models.map((m) => ({
      value: m.id,
      label: m.label,
      description: m.description ? `${m.id} · ${m.description}` : m.id,
      badge: m.inputTokenLimit ? `${Math.round(m.inputTokenLimit / 1000)}K ctx` : undefined,
      keywords: m.id,
    })),
    ...(form.model && !models.some((m) => m.id === form.model) ? [{ value: form.model, label: form.model, description: 'Current model id' }] : []),
  ];

  const pickModel = (value: string) => {
    const m = models.find((x) => x.id === value);
    setForm((f) => ({
      ...f,
      model: value,
      label: f.label && f.label !== models.find((x) => x.id === f.model)?.label ? f.label : m?.label || value,
      description: f.description || (m?.description ? m.description.slice(0, 400) : ''),
    }));
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.model) return setError('Pick a model from the catalog.');
    setSaving(true);
    setError(null);
    const body = {
      model: form.model,
      label: form.label,
      description: form.description || null,
      strengths: form.strengths || null,
      sortOrder: Number(form.sortOrder) || 0,
      isActive: form.isActive,
      isDefault: form.isDefault,
    };
    try {
      if (editing) await api.patch(`/admin/ai/providers/${id}`, body);
      else await api.post('/admin/ai/providers', body);
      navigate('/ai-settings', { replace: true });
    } catch (err) {
      setError(apiError(err, 'Could not save the model.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!p || !window.confirm(`Delete ${p.label}? Rating prompts using it switch to the default model.`)) return;
    try {
      await api.delete(`/admin/ai/providers/${p.id}`);
      navigate('/ai-settings', { replace: true });
    } catch (err) {
      setError(apiError(err, 'Could not delete.'));
    }
  };

  if (editing && detail.loading) return <LoadingRows rows={6} />;
  if (editing && !p) return <Notice tone="error">{detail.error || 'Model not found.'}</Notice>;

  return (
    <div>
      <PageHeader
        crumbs={[{ label: 'AI Settings', to: '/ai-settings' }, { label: editing ? p!.label : 'New model' }]}
        title={editing ? `Edit ${p!.label}` : 'Add model'}
        actions={
          editing &&
          !p!.isDefault && (
            <button type="button" className="mz-btn-danger" onClick={() => void remove()}>
              Delete
            </button>
          )
        }
      />
      <form onSubmit={onSubmit} className="grid items-start gap-4 lg:grid-cols-[1.35fr_1fr]">
        <Card title="Model">
          <div className="space-y-4">
            <Field
              label="Model id"
              hint={
                catalog.loading
                  ? 'Loading models from Google…'
                  : catalog.data?.source === 'google'
                    ? `${models.length} models available to your API key.`
                    : 'Could not reach Google — showing common models.'
              }
            >
              <Select value={form.model} onChange={pickModel} options={options} searchable placeholder="Search catalog models…" emptyText="No model matches" />
            </Field>
            <Field label="Display name" htmlFor="ai-label" hint="Shown to admins when choosing a model for a rating prompt.">
              <input id="ai-label" required minLength={2} className="mz-input" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder="Gemini Flash" />
            </Field>
            <Field label="Description" htmlFor="ai-desc">
              <textarea id="ai-desc" rows={3} maxLength={400} className="mz-input" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            </Field>
            <Field label="Best for" htmlFor="ai-strengths" hint="A short note, e.g. “Fast, low cost — document checks”.">
              <input id="ai-strengths" maxLength={200} className="mz-input" value={form.strengths} onChange={(e) => setForm((f) => ({ ...f, strengths: e.target.value }))} />
            </Field>
          </div>
        </Card>

        <div className="space-y-4">
          <Card title="Availability">
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[13px] font-semibold">Default model</p>
                  <p className="text-xs text-[#a77d92]">Used for every rating and document check without a specific model.</p>
                </div>
                <Toggle
                  checked={form.isDefault}
                  disabled={editing && p!.isDefault}
                  onChange={(v) => setForm((f) => ({ ...f, isDefault: v, isActive: v ? true : f.isActive }))}
                  label="Default model"
                />
              </div>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[13px] font-semibold">Active</p>
                  <p className="text-xs text-[#a77d92]">Inactive models can't be selected in Ratings.</p>
                </div>
                <Toggle checked={form.isActive} disabled={form.isDefault} onChange={(v) => setForm((f) => ({ ...f, isActive: v }))} label="Active" />
              </div>
              {editing && p!.isDefault && <p className="text-xs text-[#a77d92]">Make another model the default to disable or delete this one.</p>}
              <Field label="Display order" htmlFor="ai-order">
                <input id="ai-order" type="number" min={0} max={1000} className="mz-input w-28" value={form.sortOrder} onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))} />
              </Field>
            </div>
          </Card>

          {editing && (
            <Card title="Used by rating prompts">
              {p!.prompts.length === 0 ? (
                <p className="text-[13px] text-[#a77d92]">{p!.isDefault ? 'No prompt picks it explicitly — every prompt without a model uses it as the default.' : 'No rating prompt uses this model.'}</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {p!.prompts.map((r) => (
                    <Link key={r.id} to={`/ratings/${r.countryCode}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#f6dbe7] px-2 py-1 text-xs font-medium hover:border-[#d6006e]">
                      <CodeChip code={r.countryCode} />
                      {r.countryName}
                    </Link>
                  ))}
                </div>
              )}
            </Card>
          )}
        </div>

        <div className="lg:col-span-2">
          {error && (
            <Notice tone="error" className="mb-2">
              {error}
            </Notice>
          )}
          <div className="mz-card sticky bottom-3 flex flex-wrap items-center justify-end gap-2 px-4 py-3">
            <button type="button" className="mz-btn-ghost" onClick={() => navigate('/ai-settings')}>
              Cancel
            </button>
            <button type="submit" disabled={saving} className="mz-btn-primary">
              {saving ? 'Saving…' : editing ? 'Save model' : 'Add model'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
